import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Tv, Wifi, KeyRound, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { usePlanFeature } from '@/hooks/usePlanFeature';

export function AddTvPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [storeId, setStoreId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);

  const { enabled: multiStore } = usePlanFeature('multi_store');
  const { data: stores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => apiClient.getStores().then(r => r.data),
  });
  // Mostra o select se o tenant tem multi-loja ou se já tem mais de uma loja
  // cadastrada por qualquer motivo (downgrade, master admin etc.).
  const showStoreField = multiStore || stores.length > 1;

  async function pair() {
    if (code.length !== 8) {
      toast.error('Código deve ter 8 caracteres');
      return;
    }
    setLoading(true);
    try {
      const r = await apiClient.pairDevice(code, name || undefined, location || undefined);
      const tvId = r.data.tv_id;
      // Se o usuário escolheu uma loja não-default, atualiza após o pareamento
      // (o endpoint /devices/pair ainda não aceita store_id direto).
      if (storeId) {
        try {
          await apiClient.updateTv(tvId, { store_id: storeId });
        } catch {
          toast.error('TV pareada, mas falhou ao vincular à loja. Edite a TV pra ajustar.');
        }
      }
      toast.success('TV pareada com sucesso!');
      navigate(`/tvs/${tvId}`);
    } catch (err: any) {
      const code = err.response?.data?.error;
      if (code === 'invalid_or_expired_code') toast.error('Código inválido ou expirado. Pegue um novo na TV.');
      else if (code === 'tv_limit_reached') toast.error('Limite de TVs do plano atingido. Faça upgrade.');
      else toast.error('Erro ao parear TV');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-app py-8 max-w-3xl">
      <Link to="/tvs" className="inline-flex items-center gap-1 text-sm text-muted-foreground mb-6 hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <h1 className="text-3xl font-bold">Adicionar nova TV</h1>
      <p className="text-muted-foreground mt-1">Pareamento plug-and-play em segundos</p>

      <div className="grid md:grid-cols-2 gap-8 mt-8">
        {/* Tutorial */}
        <Card>
          <CardHeader><CardTitle>Como funciona</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Step n={1} icon={Wifi} title="Conecte a TV à internet" desc="Cabo de rede ou WiFi" />
            <Step n={2} icon={Tv} title="Abra o app MyShow" desc="Já vem pré-instalado nos boxes Visual" />
            <Step n={3} icon={KeyRound} title="Anote o código de 8 caracteres" desc="Aparece grande na tela" />
            <Step n={4} icon={Check} title="Digite aqui ao lado" desc="Pronto! Conteúdo começa imediatamente" />
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardHeader><CardTitle>Código de pareamento</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">Código (8 caracteres)</label>
              <Input
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                placeholder="ABC12XYZ"
                className="text-center font-mono text-2xl tracking-widest h-16"
                maxLength={8}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nome da TV (opcional)</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: TV Caixa 01" />
            </div>
            <div>
              <label className="text-sm font-medium">Localização (opcional)</label>
              <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="Ex: Setor de bebidas" />
            </div>
            {showStoreField && (
              <div>
                <label className="text-sm font-medium">Loja</label>
                <select
                  value={storeId}
                  onChange={e => setStoreId(e.target.value ? Number(e.target.value) : '')}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">— loja default —</option>
                  {stores.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name} (#{s.code}){s.is_default ? ' • default' : ''}</option>
                  ))}
                </select>
              </div>
            )}
            <Button onClick={pair} className="w-full" loading={loading} disabled={code.length !== 8} variant="gradient">
              Parear TV
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Step({ n, icon: Icon, title, desc }: any) {
  return (
    <div className="flex gap-3">
      <div className="grid place-items-center h-9 w-9 rounded-full bg-brand-100 text-brand-700 font-bold shrink-0">{n}</div>
      <div>
        <div className="font-medium flex items-center gap-2"><Icon className="h-4 w-4" />{title}</div>
        <div className="text-sm text-muted-foreground">{desc}</div>
      </div>
    </div>
  );
}
